class SolenaCarousel extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.viewport = this.querySelector('.tx-slides');
    this.slides = [...this.querySelectorAll('.tx-slide')];
    this.dots = [...this.querySelectorAll('[data-carousel-dot]')];

    if (!this.viewport || !this.slides.length) return;

    this.abortController = new AbortController();
    this.mobileQuery = window.matchMedia('(max-width: 700px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('on')));
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.delay = Math.max(3000, Number(this.dataset.autoplayDelay) || 5500);
    this.autoplayStopped = this.reducedMotionQuery.matches;
    this.userInteractedOnMobile = false;
    this.isVisible = true;
    this.isHovered = false;
    this.hasFocus = false;

    const { signal } = this.abortController;
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.addEventListener('scroll', (event) => this.onScroll(event), { capture: true, passive: true, signal });
    this.addEventListener('touchstart', () => this.onTouchStart(), { passive: true, signal });
    this.addEventListener('pointerenter', () => this.setHovered(true), { signal });
    this.addEventListener('pointerleave', () => this.setHovered(false), { signal });
    this.addEventListener('focusin', () => this.setFocused(true), { signal });
    this.addEventListener('focusout', (event) => this.setFocused(this.contains(event.relatedTarget)), { signal });
    this.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });
    document.addEventListener('visibilitychange', () => this.schedule(), { signal });
    this.mobileQuery.addEventListener('change', () => this.onViewportChange(), { signal });

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting;
        this.schedule();
      },
      { threshold: 0.2 }
    );
    this.intersectionObserver.observe(this);

    this.syncSlides();
    this.schedule();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.intersectionObserver?.disconnect();
    window.clearTimeout(this.timer);
    window.clearTimeout(this.scrollTimer);
    this.abortController = null;
  }

  onClick(event) {
    const control = event.target.closest('[data-carousel-action], [data-carousel-dot]');
    if (!control || !this.contains(control)) return;

    const direction = control.dataset.carouselAction;
    const targetIndex = control.dataset.carouselDot;

    if (direction === 'previous') this.show(this.index - 1, { userInitiated: true });
    if (direction === 'next') this.show(this.index + 1, { userInitiated: true });
    if (targetIndex !== undefined) this.show(Number(targetIndex), { userInitiated: true });
  }

  onKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (event.target.matches('input, select, textarea')) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.show(this.index - 1, { userInitiated: true });
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.show(this.index + 1, { userInitiated: true });
    }
  }

  onScroll(event) {
    if (event.target !== this.viewport || !this.mobileQuery.matches) return;

    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      const nextIndex = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
      this.index = Math.max(0, Math.min(this.slides.length - 1, nextIndex));
      this.syncSlides();
    }, 80);
  }

  onTouchStart() {
    if (!this.mobileQuery.matches) return;
    this.userInteractedOnMobile = true;
    this.schedule();
  }

  onBlockSelect(event) {
    const slide = event.target.closest('.tx-slide');
    const slideIndex = this.slides.indexOf(slide);
    if (slideIndex < 0) return;

    this.autoplayStopped = true;
    this.show(slideIndex, { scroll: true });
  }

  onViewportChange() {
    this.userInteractedOnMobile = false;
    this.syncSlides();
    if (this.mobileQuery.matches) this.scrollToCurrent('auto');
    this.schedule();
  }

  setHovered(value) {
    this.isHovered = value;
    this.schedule();
  }

  setFocused(value) {
    this.hasFocus = value;
    this.schedule();
  }

  show(index, { scroll = this.mobileQuery.matches, userInitiated = false } = {}) {
    this.index = (index + this.slides.length) % this.slides.length;
    this.syncSlides();

    if (scroll) {
      const behavior = userInitiated && !this.reducedMotionQuery.matches ? 'smooth' : 'auto';
      this.scrollToCurrent(behavior);
    }

    this.schedule();
  }

  scrollToCurrent(behavior) {
    this.viewport.scrollTo({ left: this.slides[this.index].offsetLeft, behavior });
  }

  syncSlides() {
    const exposeAllSlides = this.mobileQuery.matches;

    this.slides.forEach((slide, index) => {
      const isCurrent = index === this.index;
      slide.classList.toggle('on', isCurrent);
      slide.setAttribute('aria-hidden', String(!exposeAllSlides && !isCurrent));
      slide.toggleAttribute('inert', !exposeAllSlides && !isCurrent);
    });

    this.dots.forEach((dot, index) => {
      const isCurrent = index === this.index;
      dot.classList.toggle('on', isCurrent);
      dot.setAttribute('aria-current', isCurrent ? 'true' : 'false');
    });
  }

  schedule() {
    window.clearTimeout(this.timer);
    if (
      !this.autoplay ||
      this.autoplayStopped ||
      this.userInteractedOnMobile ||
      !this.isVisible ||
      this.isHovered ||
      this.hasFocus ||
      document.hidden
    ) return;

    this.timer = window.setTimeout(() => this.show(this.index + 1), this.delay);
  }
}

if (!customElements.get('solena-carousel')) {
  customElements.define('solena-carousel', SolenaCarousel);
}
